/**
 * Country-info REST API — mirrors backend /country-info/*.
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';
import { iso2FromCountryName } from '../utils/countryInfoLookups';

export interface CountryDataDto {
  country: string;
  iso2: string;
  isActive: boolean;
  vaccinesRequired?: string | null;
  vaccinesRecommended?: string | null;
  routineVaccinesRecommended?: string | null;
  healthRisks?: string | null;
  malariaChemo?: string | null;
  entryRequirementsSummary?: string | null;
  foodWaterSafety?: string | null;
  insectBitePrevention?: string | null;
  healthSources?: string | null;
  healthLastChecked?: string | null;
  healthConfidence?: string | null;
  currencyName?: string | null;
  currencyCode?: string | null;
  phoneCode?: string | null;
  rideHailCompanies?: string | null;
  rideHailSources?: string | null;
  rideHailLastChecked?: string | null;
}

export interface CurrencyDto {
  country: string;
  iso2: string;
  currencyCode: string;
}

export interface EmbassyDto {
  sendingCountry: string;
  hostCountry: string;
  sendingCountryISO2?: string | null;
  hostCountryISO2?: string | null;
  city: string;
  mainPhoneInternational?: string | null;
  mainPhoneLocal?: string | null;
  afterHoursPhone?: string | null;
  address?: string | null;
  embassyURL?: string | null;
  isActive: boolean;
}

export interface PortDto {
  portId: string;
  officialName: string;
  country: string;
  phonePrimary?: string | null;
  phoneAfterHours?: string | null;
  address?: string | null;
  website?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  latitudeGeocoded?: number | null;
  longitudeGeocoded?: number | null;
  isActive: boolean;
}

export interface CountryInfoDetailDto {
  country: CountryDataDto;
  currency?: CurrencyDto | null;
  embassies: EmbassyDto[];
}

async function countryInfoGet<T>(path: string): Promise<T> {
  const headers = await buildAuthHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, { method: 'GET', headers });
  if (!response.ok) {
    throw new Error(`country-info GET ${path} failed: ${response.status}`);
  }
  return (await response.json()) as T;
}

export type ReverseGeocodeCountryResult = {
  countryISO2: string;
  countryName?: string | null;
};

export async function reverseGeocodeCountry(coords: {
  latitude: number;
  longitude: number;
}): Promise<ReverseGeocodeCountryResult | null> {
  const { latitude, longitude } = coords;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${encodeURIComponent(String(latitude))}&longitude=${encodeURIComponent(String(longitude))}&localityLanguage=en`;
  const response = await fetch(url);
  if (!response.ok) return null;
  const body = (await response.json()) as { countryCode?: string; countryName?: string };
  const fromCode = body.countryCode?.trim().toUpperCase();
  const countryName = body.countryName?.trim() || null;
  if (fromCode && /^[A-Z]{2}$/.test(fromCode)) {
    return { countryISO2: fromCode, countryName };
  }
  const fromName = iso2FromCountryName(countryName);
  if (!fromName) return null;
  return { countryISO2: fromName, countryName };
}

export async function reverseGeocodeCountryIso2(coords: {
  latitude: number;
  longitude: number;
}): Promise<string | null> {
  const result = await reverseGeocodeCountry(coords);
  return result?.countryISO2 ?? null;
}

export async function resolveCountryIso2(input: {
  countryISO2?: string | null;
  countryName?: string | null;
  coords?: { latitude: number; longitude: number } | null;
}): Promise<{ code: string | null; via: 'stored' | 'name' | 'geocode' | 'none' }> {
  const stored = input.countryISO2?.trim().toUpperCase() || null;
  if (stored && /^[A-Z]{2}$/.test(stored)) return { code: stored, via: 'stored' };

  const fromName = iso2FromCountryName(input.countryName);
  if (fromName) return { code: fromName, via: 'name' };

  if (input.coords) {
    try {
      const fromGeo = await reverseGeocodeCountryIso2(input.coords);
      if (fromGeo) return { code: fromGeo, via: 'geocode' };
    } catch {
      // fall through
    }
  }

  return { code: null, via: 'none' };
}

export async function fetchCountryInfoDetail(
  iso2: string,
  citizenIso2?: string | null
): Promise<CountryInfoDetailDto> {
  const code = iso2.trim().toUpperCase();
  const params = new URLSearchParams();
  if (citizenIso2 && citizenIso2.trim()) {
    params.set('citizen', citizenIso2.trim().toUpperCase());
  }
  const qs = params.toString();
  return countryInfoGet<CountryInfoDetailDto>(
    `/country-info/countries/${encodeURIComponent(code)}${qs ? `?${qs}` : ''}`
  );
}

export async function fetchCountryPorts(countryKey: string): Promise<PortDto[]> {
  const key = countryKey.trim();
  const url = `/country-info/ports?country=${encodeURIComponent(key)}`;
  const body = await countryInfoGet<PortDto[] | { ports?: PortDto[] }>(url);
  if (Array.isArray(body)) return body;
  return Array.isArray(body.ports) ? body.ports : [];
}

/** Haversine distance in km. */
export function distanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export function pickNearestPort(
  ports: PortDto[],
  coords: { latitude: number; longitude: number } | null | undefined,
  cityName?: string | null
): PortDto | null {
  const active = ports.filter((p) => p.isActive !== false);
  if (active.length === 0) return null;

  if (coords && Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude)) {
    let best: PortDto | null = null;
    let bestKm = Number.POSITIVE_INFINITY;
    for (const port of active) {
      const lat = port.latitude ?? port.latitudeGeocoded;
      const lon = port.longitude ?? port.longitudeGeocoded;
      if (lat == null || lon == null) continue;
      const km = distanceKm(coords.latitude, coords.longitude, lat, lon);
      if (km < bestKm) {
        bestKm = km;
        best = port;
      }
    }
    if (best && bestKm <= 80) return best;
  }

  const name = (cityName || '').trim().toLowerCase();
  if (name) {
    const byName = active.find((p) => p.officialName.toLowerCase().includes(name));
    if (byName) return byName;
  }

  return active[0] ?? null;
}
